import React from "react";
import moment from "moment";
import { Link } from "react-router";
import { Table, Tr, Td, Sort } from "reactable";
import { RaisedButton } from "material-ui";
import qs from "qs";

import WorkingIndicator from "./working-indicator.jsx";
import InlineLinkIconButton from "./inline-link-icon-button.jsx";
import LinkLike from "./link-like.jsx";
import * as util from "../../util";

import UserStore from "../../stores/UserStore";
import config from "../../config";

class GenericAnalyticsTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentPage: 0
    };

    this._makeRow = this._makeRow.bind(this);
    this.makeRows = this.makeRows.bind(this);
    this.downloadCsv = this.downloadCsv.bind(this);
    this.downloadPdf = this.downloadPdf.bind(this);
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  _makeRow(id, data, options = {}) {
    let noWrapTdStyle = { whiteSpace: "nowrap" };

    return (
      <Tr
        key={id}
        style={(options.source && options.source.rowStyle) || data.rowStyle}
      >
        {options.source ? (
          <Td column={this.props.label} value={options.source.name}>
            <span>
              {options.source.nameLabel || options.source.name}
              {options.source.id && !this.props.noLink && (
                <InlineLinkIconButton
                  onTouchTap={() => {
                    this.context.router.push(
                      `/event_buckets/${
                        this.props.eventBucket
                      }/${util.snakeCase(this.props.label)}s/${id}`
                    );
                  }}
                  tooltip={`View ${this.props.label}`}
                />
              )}
            </span>
          </Td>
        ) : null}
        {this.props.columns.map(c => {
          let value, label, itemStyle;

          if (c.value && typeof c.value === "function") {
            value = label = c.value(data);
          } else if (data[c.value] && typeof data[c.value] === "object") {
            ({ value, label, itemStyle } = data[c.value]);
          } else {
            value = label = data[c.value];
          }

          itemStyle = Object.assign(itemStyle || data.itemStyle || {}, c.style);

          return (
            <Td column={c.label} value={value} style={itemStyle}>
              {label}
            </Td>
          );
        })}
      </Tr>
    );
  }

  makeRows(data) {
    return [
      (this.props.source ? [this.props.label] : []).concat(
        this.props.columns.map(c => c.label)
      )
    ].concat(
      data.map(v => {
        return (
          this.props.source
            ? [
                (
                  v._source ||
                  this.props.source[v.id] || {
                    name: `Deleted ${this.props.label.toLowerCase()}`
                  }
                ).name
              ]
            : []
        ).concat(
          this.props.columns.map(c => {
            return c.value && typeof c.value === "function"
              ? c.value(v)
              : v[c.value] && typeof v[c.value].label !== "undefined"
              ? typeof v[c.value].csv_label !== "undefined"
                ? v[c.value].csv_label
                : typeof v[c.value].label !== "object"
                ? v[c.value].label
                : v[c.value].value
              : v[c.value];
          })
        );
      })
    );
  }

  downloadCsv(data) {
    util.clientSideDownload(
      `${this.props.csvLabel}-${
        this.props.csvLabelDetail || util.snakeCase(this.props.eventBucket.name)
      }.csv`,
      "text/csv",
      util.makeCsv(this.makeRows(data))
    );
  }

  downloadPdf(data) {
    const rows = this.makeRows(data),
      options = {
        filename: this.props.pdfFilename,
        title: this.props.pdfTitle,
        subtitle: this.props.pdfSubtitle,
        header: rows[0],
        rows: rows.slice(1)
      };

    const form = document.createElement("form");
    form.method = "post";
    form.action = `${
      config.endpoint
    }reports/generated.pdf?${UserStore.makeApiKeyQueryString()}`;
    form.target = "generatedPdf";

    const input = document.createElement("input");
    input.name = "options";
    input.type = "hidden";
    input.value = JSON.stringify(options);
    form.appendChild(input);

    document.body.appendChild(form);

    window.open("", "generatedPdf");
    form.submit();
  }

  render() {
    let {
      data,
      label,
      eventBucket,
      source,
      defaultSort,
      defaultSortInvert,
      noSort,
      columns
    } = this.props;

    let dataArray = Array.isArray(data)
      ? data
      : Object.keys(data).map(k => Object.assign({ id: k }, data[k]));

    let rows = dataArray.map(v => {
      return this._makeRow(v.id, v, {
        source:
          v._source ||
          (source
            ? source[v.id] || { name: `Deleted ${label.toLowerCase()}` }
            : undefined)
      });
    });

    let columnLabels = (source ? [label] : []).concat(
      columns.map(c => c.label)
    );

    return (
      <div>
        <Table
          className="bordered striped responsive-table"
          sortable={noSort ? undefined : columnLabels}
          defaultSort={
            defaultSort
              ? {
                  column: defaultSort,
                  direction: defaultSortInvert ? "asc" : "desc"
                }
              : undefined
          }
          columns={columnLabels}
          itemsPerPage={
            this.props.itemsPerPage && rows.length > this.props.itemsPerPage
              ? this.props.itemsPerPage
              : undefined
          }
          currentPage={
            this.props.itemsPerPage ? this.state.currentPage : undefined
          }
          onPageChange={
            this.props.itemsPerPage
              ? p => this.setState({ currentPage: p })
              : undefined
          }
        >
          {rows}
        </Table>
        {this.props.loading ? (
          <div style={this.props.loadingStyle || { minHeight: "70vh" }}>
            <WorkingIndicator centered style={{ margin: "1em" }} />
          </div>
        ) : null}
        {!this.props.noDownload ? (
          <div
            style={Object.assign(
              { marginTop: "1em" },
              this.props.downloadContainerStyle
            )}
          >
            {this.props.downloadSubtle ? (
              <LinkLike onTouchTap={() => this.downloadCsv(dataArray)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  style={{
                    width: 16,
                    height: 16,
                    marginRight: 4,
                    verticalAlign: -4
                  }}
                >
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Download (.csv)
              </LinkLike>
            ) : (
              <RaisedButton
                label="Download (.csv)"
                onTouchTap={() => this.downloadCsv(dataArray)}
              />
            )}
            {this.props.showPdfDownload ? (
              <RaisedButton
                style={{ marginLeft: 7 }}
                label="Download (.pdf)"
                onTouchTap={() => this.downloadPdf(dataArray)}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
}

export default GenericAnalyticsTable;



// WEBPACK FOOTER //
// ./src/app/components/partials/generic-analytics-table.jsx